import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, Download, FileText, Loader2, User, X } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../auth/context';
import type { AgreementResponse } from '../types';

interface MsaAcceptanceModalProps {
  onAccepted: () => void;
  onCancel?: () => void;
}

export function MsaAcceptanceModal({ onAccepted, onCancel }: MsaAcceptanceModalProps) {
  const { session } = useAuth();
  const [agreement, setAgreement] = useState<AgreementResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const contributorName = session?.displayName?.trim() || '';
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    api.acquisitionAgreementActive()
      .then((data) => {
        if (mounted) {
          setAgreement(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err.message || 'Failed to load Master Services Agreement.');
          setLoading(false);
        }
      });
    return () => { mounted = false; };
  }, []);

  const handleDownloadPdf = async () => {
    setError('');
    try {
      const url = URL.createObjectURL(await api.acquisitionAgreementPdf());
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Esolution_Otomashen_Freelancer_MSA_v2026.1.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to download the agreement PDF.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contributorName || !agreed) return;

    setSubmitting(true);
    setError('');
    try {
      await api.acquisitionAgreementAccept({
        signatureText: contributorName,
        evidenceJson: {
          submittedFromUrl: window.location.href,
          userAgent: navigator.userAgent,
          signedDate: new Date().toISOString(),
        },
      });
      window.dispatchEvent(new CustomEvent('msa-accepted', { detail: { hasAccepted: true } }));
      window.dispatchEvent(new CustomEvent('agreement-status-changed', { detail: { hasAccepted: true } }));
      onAccepted();
    } catch (err: any) {
      setError(err.message || 'Failed to record agreement acceptance. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-backdrop msa-modal-backdrop">
        <div className="modal-card modal-card-sm text-center py-8">
          <Loader2 className="spin mx-auto text-primary" size={32} />
          <p className="mt-4 text-secondary">Loading Master Services Agreement...</p>
        </div>
      </div>
    );
  }

  const docTitle = agreement?.title || 'Esolution Otomashen Freelancer Data Contributor Master Services Agreement (MSA)';
  const docVersion = agreement?.version || '2026.1';
  const payoutDays = agreement?.payoutWindowDays || 15;

  return (
    <div className="modal-backdrop msa-modal-backdrop" role="dialog" aria-modal="true">
      <div className="msa-split-modal">
        {/* Left Column: Illustration & Vertical Stepper */}
        <aside className="msa-split-aside">
          <div className="msa-illustration-container">
            <svg
              className="msa-illustration-svg"
              width="145"
              height="115"
              viewBox="0 0 190 150"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              {/* Soft sparkles */}
              <path d="M25 35L27 28L29 35L36 37L29 39L27 46L25 39L18 37L25 35Z" fill="#86efac" opacity="0.85" />
              <path d="M165 40L167 34L169 40L175 42L169 44L167 50L165 44L159 42L165 40Z" fill="#86efac" opacity="0.85" />

              {/* Organic leaves */}
              <path d="M38 78C30 65 34 50 48 46C56 60 52 75 38 78Z" fill="#6ee7b7" opacity="0.85" />
              <path d="M32 95C22 84 24 68 38 64C48 78 44 92 32 95Z" fill="#34d399" opacity="0.9" />
              <path d="M152 75C160 62 156 48 142 44C134 58 138 73 152 75Z" fill="#6ee7b7" opacity="0.85" />
              <path d="M158 92C168 81 166 65 152 61C142 75 146 89 158 92Z" fill="#34d399" opacity="0.9" />

              {/* Document sheet */}
              <g filter="url(#docDropShadow)">
                <rect x="52" y="24" width="86" height="106" rx="10" fill="#FFFFFF" />
                <path d="M124 24H138V38L124 24Z" fill="#F1F5F9" />
                <path d="M124 24V38H138" stroke="#CBD5E1" strokeWidth="1" strokeLinejoin="round" />
              </g>

              {/* Document Header "MSA" */}
              <text x="95" y="47" textAnchor="middle" fill="#047857" fontSize="15" fontWeight="800" fontFamily="sans-serif" letterSpacing="0.05em">
                MSA
              </text>

              {/* Document Text Skeleton Lines */}
              <rect x="64" y="56" width="62" height="4" rx="2" fill="#E2E8F0" />
              <rect x="64" y="65" width="62" height="4" rx="2" fill="#E2E8F0" />
              <rect x="64" y="74" width="46" height="4" rx="2" fill="#E2E8F0" />
              <rect x="64" y="83" width="54" height="4" rx="2" fill="#E2E8F0" />
              <rect x="64" y="92" width="38" height="4" rx="2" fill="#E2E8F0" />

              {/* Green circular shield with white checkmark badge */}
              <g filter="url(#shieldDropShadow)">
                <circle cx="126" cy="108" r="22" fill="#059669" />
                <path d="M117 108L123 114L135 102" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
              </g>

              <defs>
                <filter id="docDropShadow" x="46" y="20" width="98" height="118" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feDropShadow dx="0" dy="4" stdDeviation="6" floodOpacity="0.09" floodColor="#0F172A" />
                </filter>
                <filter id="shieldDropShadow" x="100" y="84" width="52" height="52" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.2" floodColor="#047857" />
                </filter>
              </defs>
            </svg>
          </div>

          {/* Stepper */}
          <nav className="msa-vertical-stepper" aria-label="Agreement progress">
            <div className="msa-step-item">
              <div className="msa-step-badge step-completed">1</div>
              <div className="msa-step-content">
                <strong className="msa-step-title">Review Agreement</strong>
                <p className="msa-step-desc">Read the terms and conditions of the Data Contributor MSA</p>
              </div>
            </div>

            <div className="msa-step-connector" />

            <div className="msa-step-item">
              <div className={`msa-step-badge ${contributorName ? 'step-completed' : 'step-neutral'}`}>2</div>
              <div className="msa-step-content">
                <strong className="msa-step-title">Add Signature</strong>
                <p className="msa-step-desc">Confirm your registered full legal name</p>
              </div>
            </div>

            <div className="msa-step-connector" />

            <div className="msa-step-item">
              <div className={`msa-step-badge ${agreed ? 'step-completed' : 'step-neutral'}`}>3</div>
              <div className="msa-step-content">
                <strong className="msa-step-title">Access Workspace</strong>
                <p className="msa-step-desc">Accept the agreement to unlock your workspace</p>
              </div>
            </div>
          </nav>
        </aside>

        {/* Right Column: Header, Scrollable Body, Form, and Actions */}
        <main className="msa-split-main">
          <div className="msa-header-section">
            <div className="msa-header-icon-badge">
              <FileText size={22} />
            </div>
            <div className="msa-header-text">
              <h2 className="msa-modal-heading">{docTitle}</h2>
              <p className="msa-modal-subheading">
                Version {docVersion} · Effective Oct 1, 2026
              </p>
            </div>
            {onCancel && (
              <button
                type="button"
                className="msa-close-btn"
                onClick={onCancel}
                aria-label="Close agreement dialog"
              >
                <X size={18} />
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="msa-form-container">
            <div className="msa-split-body">
              {error && (
                <div className="banner banner-error mb-3">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              {/* Agreement Document Card */}
              <section className="msa-section-block">
                <h3 className="msa-section-title">Agreement Document</h3>
                <div className="msa-document-card">
                  <div className="msa-document-meta-left">
                    <div className="msa-pdf-badge" aria-hidden="true">
                      <svg width="26" height="32" viewBox="0 0 28 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 4C0 1.79086 1.79086 0 4 0H18L28 10V30C28 32.2091 26.2091 34 24 34H4C1.79086 34 0 32.2091 0 30V4Z" fill="#EF4444"/>
                        <path d="M18 0V10H28L18 0Z" fill="#DC2626"/>
                        <text x="14" y="24" textAnchor="middle" fill="#FFFFFF" fontSize="9" fontWeight="800" fontFamily="sans-serif">PDF</text>
                      </svg>
                    </div>
                    <div className="msa-doc-file-details">
                      <div className="msa-doc-file-title">{docTitle}</div>
                      <div className="msa-doc-file-info">
                        Version {docVersion} · Effective Oct 1, 2026 · PDF (2.4 MB)
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="msa-download-pdf-btn"
                    onClick={handleDownloadPdf}
                  >
                    <Download size={14} />
                    <span>Download PDF</span>
                  </button>
                </div>
              </section>

              {/* Electronic Signature & Legal Details */}
              <section className="msa-section-block">
                <h3 className="msa-section-title">Electronic Signature</h3>
                <p className="msa-section-helper">
                  Your registered freelancer name is used as your electronic signature. This is a legally binding agreement.
                </p>

                <div className="msa-signature-input-wrap">
                  <User size={16} className="msa-signature-icon" />
                  <input
                    id="msa-signature-name"
                    type="text"
                    className="msa-signature-input"
                    value={contributorName}
                    readOnly
                    aria-label="Contributor full legal name"
                    required
                  />
                  <span className="msa-signature-verified-pill">
                    <CheckCircle2 size={13} />
                    <span>Verified Legal Signatory</span>
                  </span>
                </div>

                {/* Legal Summary / Audit Card */}
                <div className="msa-legal-meta-card">
                  <div className="msa-legal-meta-grid">
                    <div className="msa-legal-meta-item">
                      <span className="msa-legal-meta-label">Company</span>
                      <span className="msa-legal-meta-val">Esolution Otomashen</span>
                    </div>
                    <div className="msa-legal-meta-item">
                      <span className="msa-legal-meta-label">Registered Address</span>
                      <span className="msa-legal-meta-val">Noida, India</span>
                    </div>
                    <div className="msa-legal-meta-item">
                      <span className="msa-legal-meta-label">Legal Signatory</span>
                      <span className="msa-legal-meta-val">{contributorName}</span>
                    </div>
                    <div className="msa-legal-meta-item">
                      <span className="msa-legal-meta-label">Agreement Version</span>
                      <span className="msa-legal-meta-val">Version {docVersion}</span>
                    </div>
                  </div>
                  <div className="msa-legal-meta-footer">
                    <span className="msa-legal-meta-notice">
                      <strong>Electronic acceptance:</strong> I confirm that I have read, understood, and agree to this Agreement.
                    </span>
                  </div>
                </div>

                {/* Consent Checkbox */}
                <div
                  className={`msa-consent-checkbox-card ${agreed ? 'is-agreed' : ''}`}
                  onClick={() => setAgreed(!agreed)}
                >
                  <input
                    id="msa-consent-checkbox"
                    type="checkbox"
                    className="msa-consent-checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    required
                  />
                  <label
                    htmlFor="msa-consent-checkbox"
                    className="msa-consent-label"
                    onClick={(e) => e.stopPropagation()}
                  >
                    I have read, understood, and agree to be bound by this Master Services Agreement (v{docVersion}),
                    including the data provenance warrants and the <strong>{payoutDays}-business-day payment timeline</strong> following technical QC approval.
                  </label>
                </div>
              </section>
            </div>

            {/* Modal Actions pinned at bottom */}
            <div className="msa-modal-actions">
              {onCancel && (
                <button
                  type="button"
                  className="msa-btn-cancel"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="msa-btn-confirm"
                disabled={submitting || !contributorName || !agreed}
              >
                {submitting ? (
                  <>
                    <Loader2 className="spin" size={17} />
                    <span>Accepting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={17} />
                    <span>Accept Agreement & Unlock Workspace</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}
